import { Injectable } from '@angular/core';

import MVT from 'ol/format/MVT';
import WKT from 'ol/format/WKT';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import { Fill, RegularShape, Stroke, Style, Text, Icon, Circle } from 'ol/style';
import { get as getProjection } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Point, Polygon } from 'ol/geom';
import Modify from 'ol/interaction/Modify';
import Snap from 'ol/interaction/Snap';
import Collection from 'ol/Collection';
import Feature from 'ol/Feature';

import { CommonRequestService, PagingParam } from './common-request.service';
import { LRSMap } from 'assets/TS/LRSMap_New';
import { Gfo, GfoService, ColumnFilter } from './gfo.service';
import { UserService } from './user.service';
import { Gto, GtoService, GtoProperty } from './gto.service';
import { AppinfoService, DataColumn } from './appinfo.service';
import { CommonMapService, VectorTileGridInfoByLevel } from './common-map.service';
import { ToastrService } from 'ngx-toastr';
import { EditService } from './edit.service';
import { RequestTableService } from './request-table.service';
import { AttrIndexService } from './attr-index.service';

@Injectable({
	providedIn: 'root'
})
export class DemarcationService {

	public readonly demarcationSourceTableName = 'azgiv.demarcation_points';
	public readonly demarcationSourceTableViewName = 'azgiv.v_demarcation_points';
	// Layers
	public readonly dmLayerName = 'dmLayer';

	// public readonly demarcationLayerName = 'demarcationLayer';
	public readonly demarcationBackgroundLayerName = 'demarcationBgLayer';
	// public readonly demarcationSelectionLayerName = 'demarcationSelectionLayer';
	// Columns
	public dmAgencyColumn = 'mpt_affected_agencies';
	public dmGeomColumn = 'mpt_geom';
	public dmTranxCreateColumn = 'mpt_tranx_create';
	public dmTranxDeleteColumn = 'mpt_tranx_delete';
	public dmResolvedColumn = 'mpt_resolved';
	public dmStateCodeColumn = 'mpt_state_cd';
	public dmGuidColumn = 'mpt_guid';
	public dmIdColumn: string; // Prime key col
	public coordColumn = 'coord';

	public sourceTableColumns: DataColumn[];

	public vectorTileInfo: {
		srid: string;
		extent: number[];
		numGridLevel: number;
		grids: VectorTileGridInfoByLevel
	}
	// Count tiles to know when all the tiles are loaded
	// https://stackoverflow.com/questions/33061221/ensuring-all-tiles-are-loaded-in-open-layers-3-xyz-source
	public numOfInFlightTiles = 0;
	constructor(
		private toastr: ToastrService,
		private appinfoService: AppinfoService,
		private attrIndexService: AttrIndexService,
		private cMapService: CommonMapService,
		private editService: EditService,
		private requestService: CommonRequestService,
		private gfoService: GfoService,
		private gtoService: GtoService,
		private requestTableService: RequestTableService,
		private userService: UserService
	) {
		this.initSourceTableColumns();
		this.dmIdColumn = this.sourceTableColumns.find(c => c.isPrimeKey).name;
	}

	private initSourceTableColumns() {
		const colNames = Object.keys(this.appinfoService.columnMap).filter(c => this.appinfoService.columnMap[c].sourceTab === this.demarcationSourceTableName);
		this.sourceTableColumns = colNames.map(n => this.appinfoService.columnMap[n]);
	}

	public addDemarcationLayer(map: LRSMap.Map) {
		map.addLayer(this.dmLayerName, {
			style: this.demarcationLayerStyle
		});
	}
	demarcationLayerStyle = (feat) => {
		let imageStyle;
		let fillColor = 'darkblue';
		let radius = 5;
		const props = feat.getProperties();
		// Set different color for unresolved data
		if (props[this.attrIndexService.isEditedKey]) {
			fillColor = 'deeppink'; // 'cyan';
			radius = 8;
		} else if (!props[this.dmResolvedColumn]) { // Unresolved
			fillColor = 'red';
			radius = 8;
		}
		// Set different shape according to agencies involved
		const agencies = this.getAgencyListFromStr(feat.getProperties()[this.dmAgencyColumn]);
		if (agencies.length === 1 && agencies[0] === this.userService.getUserInfo().customerId.toString()) {
			// Triangle
			imageStyle = new RegularShape({
				fill: new Fill({
					color: fillColor
				}),
				points: 3,
				radius: radius,
				// rotation: Math.PI / 4,
				angle: 0,
			});
		} else {
			// Diamond
			imageStyle =  new RegularShape({
				fill: new Fill({
					color: fillColor
				}),
				points: 4,
				radius: radius,
			})
		}
		const defaultStyle = new Style({
			stroke: new Stroke({
				width: 2,
				color: 'rgba(0, 102, 204)'
			}),
			image: imageStyle
		});
		return [defaultStyle];
	}

	// public async getMatchPointsFromTileGrid(tileGrid, ext, zoom, isOtherAgencyOnly?: boolean, filters?: ColumnFilter[]) {
	// 	let attrList;
	// 	const agencyCol = this.appinfoService.columnMap[this.dmAgencyColumn];
	// 	let agencyFilter: ColumnFilter;
	// 	filters = filters || [];
	// 	if (!isOtherAgencyOnly) {
	// 		// Gfo to get tiles
	// 		agencyFilter = this.gfoService.getFilter(agencyCol.name, this.appinfoService.filterOperators.contains.name, this.userService.getUserInfo().customerId, agencyCol.type);
	// 		filters.push(agencyFilter);
	// 		//
	// 		attrList = '*';
	// 	} else {
	// 		agencyFilter = this.gfoService.getFilter(agencyCol.name, this.appinfoService.filterOperators['not contains'].name, this.userService.getUserInfo().customerId, agencyCol.type);
	// 		filters.push(agencyFilter);
	// 		//
	// 		const cols = [
	// 			this.dmStateCodeColumn,
	// 			this.dmAgencyColumn]; // TODO
	// 		attrList = cols.join(',');
	// 	}
	// 	const features = await this.editService.getFeaturesFromTileGrid(tileGrid, ext, zoom, this.demarcationSourceTableViewName, filters, attrList);
	// 	return features;
	// }
	public async getMatchPointsFromTable(paging: PagingParam, filters?: ColumnFilter[]) {
		filters = filters || [];
		// Add Agency filter
		const agencyCol = this.appinfoService.columnMap[this.dmAgencyColumn];
		const agencyFilter = this.gfoService.getFilter(agencyCol.name, this.appinfoService.filterOperators.contains.name, this.userService.getUserInfo().customerId, agencyCol.type);
		filters.push(agencyFilter);
		const gfo = this.gfoService.getGfo(this.gfoService.filterLogics.and, filters);
		const rsp = await this.requestTableService.tableGet(this.demarcationSourceTableViewName, null, gfo, paging);
		return rsp;
	}


// Updates
	public addGtoProp(val, col: string, isGeom?: boolean, prop?: GtoProperty, colType?: string): GtoProperty {
		prop = prop || {};
		if (isGeom) {
			prop[this.dmGeomColumn] = {
				type: 'geometry',
				value: 'ST_GeomFromText(\'\'POINT(' + val[0] + ' ' + val[1] + ')\'\', 3857)'
			}
		} else {
			prop[col] = {
				type: colType || this.appinfoService.columnMap[col].type,
				value: val
			}
		}
		return prop;	
	}
	public getDeletePointsGto(whereClause?: string, ids?: any[]) {
		const prop: GtoProperty = {};
		// Update TranxDelete column
		this.addGtoProp('<p_session_id>', this.dmTranxDeleteColumn, false, prop, this.appinfoService.columnMap[this.dmTranxDeleteColumn].type);
		//
		const gto: Gto = {
			table: this.demarcationSourceTableName,
			operation: 'update',
			properties: [prop]
		};
		if (ids && ids.length) {
			const idCol = this.appinfoService.columnMap[this.dmIdColumn];
			gto.keycol = idCol.name;
			gto.keytyp = idCol.type;
			gto.keyvals = ids;
		}
		if (whereClause) {
			gto.where = whereClause;
		}
		return gto;
	}
	// Do not include geom/id/uid col in attrCols
	public getAddPointsGto(records, attrCols: string[]) {
		const props: GtoProperty[] = [];
		records.forEach(r => {
			const prop: GtoProperty = {};
			// Add attribute
			attrCols.forEach(c => {
				this.addGtoProp(r[c], c, false, prop);
			});
			// Add geometry
			this.addGtoProp(r[this.coordColumn], null, true, prop);
			// Add tranx create
			this.addGtoProp('<p_session_id>', this.dmTranxCreateColumn, false, prop);
			//
			props.push(prop);
		});

		const gto: Gto = {
			table: this.demarcationSourceTableName,
			operation: 'insert',
			properties: props
		};
		return gto;
	}
	// Include id cols
	public getUpdatePointsGtosByRecords(cols: string[], records: any[], originalRecords: any[]) {
		const idCol = this.appinfoService.columnMap[this.dmIdColumn];
		const ids = [];
		const retireProps: GtoProperty[] = [];
		const insertProps: GtoProperty[] = [];
		records.forEach((r, idx) => {
			ids.push(r[idCol.name]);
			// Retire
			const retireProp: GtoProperty = {};
			this.addGtoProp('<p_session_id>', this.dmTranxDeleteColumn, false, retireProp);
			retireProps.push(retireProp);
			// Insert
			const insertProp: GtoProperty = {};
			// Compare geom col
			const hasGeomChanged = this.hasCoordChanged(r[this.coordColumn], originalRecords[idx][this.coordColumn]);
			if (hasGeomChanged) {
				this.addGtoProp(r[this.coordColumn], null, true, insertProp);
			}
			cols.forEach(c => {
				// Do not insert primary key column / tranx delete column
				if (c === idCol.name || c === this.dmTranxDeleteColumn) {
					return;
				}
				// If geometry has changed, do not insert geom col since it's handled above
				if (hasGeomChanged && c === this.dmGeomColumn) {
					return;
				}
				// If it's the resolve column, set it to false if it's multi agency
				if (c === this.dmResolvedColumn) {
					const agencyList = this.getAgencyListFromStr(r[this.dmAgencyColumn]);
					if (agencyList.length > 1) {
						this.addGtoProp(false, c, false, insertProp);
						return;
					}
				}
				const hasChanged = this.hasValueChanged(r[c], originalRecords[idx][c])
				if (hasChanged) {
					this.addGtoProp(r[c], c, false, insertProp);
				} else {
					this.addGtoProp(c, c, false, insertProp, 'field');
				}
			});
			this.addGtoProp('<p_session_id>', this.dmTranxCreateColumn, false, insertProp);
			insertProps.push(insertProp);
		});
		const gtoRetire: Gto = {
			table: this.demarcationSourceTableName,
			operation: 'update',
			keycol: idCol.name,
			keytyp: idCol.type,
			keyvals: ids,
			properties: retireProps
		};
		const gtoInsert: Gto = {
			table: this.demarcationSourceTableName,
			operation: 'insert',
			keycol: idCol.name,
			keytyp: idCol.type,
			keyvals: ids,
			properties: insertProps
		};
		const gtos = [gtoRetire, gtoInsert];
		return gtos;
	}
	// Include id cols
	public getUpdatePointsGtosByWhereClause(updatedCols: {[col: string]: any}, cols: string[], whereClause: string) {
		// Retire
		const retireProp: GtoProperty = {};
		this.addGtoProp('<p_session_id>', this.dmTranxDeleteColumn, false, retireProp);
		// Insert
		const insertProp: GtoProperty = {}
		// Add existing values as 'field'
		cols.forEach(c => {
			if (updatedCols.hasOwnProperty(c)) {
				if (c === this.coordColumn) {
					this.addGtoProp(updatedCols[c], null, true, insertProp);
				} else {
					this.addGtoProp(updatedCols[c], c, false, insertProp);
				}
			} else {
				this.addGtoProp(c, c, false, insertProp, 'field');
			}
		});
		// Add tranx create
		this.addGtoProp('<p_session_id>', this.dmTranxCreateColumn, false, insertProp);
		const gtoRetire: Gto = {
			table: this.demarcationSourceTableName,
			operation: 'update',
			where: whereClause,
			properties: [retireProp]
		};
		const gtoInsert: Gto = {
			table: this.demarcationSourceTableName,
			operation: 'insert',
			where: whereClause,
			properties: [insertProp]
		};
		const gtos = [gtoRetire, gtoInsert];
		return gtos;
	}
	public hasValueChanged(val, originalVal) {
		if (val === originalVal) {
			return false;
		}
		// Check date
		if (typeof val.getTime === 'function' && typeof originalVal.getTime === 'function') {
			return val.getTime() !== originalVal.getTime();
		} else {
			return true;
		}
	}
	public hasCoordChanged(coord: number[], originalCoord: number[]) {
		if (coord[0] === originalCoord[0] && coord[1] === originalCoord[1]) {
			return false;
		} else {
			return true;
		}
	}
	public getAgencyListFromStr(agencyStr: string): string[] {
		agencyStr = agencyStr || '';
		return agencyStr.split(',')
	}

	// Update = Retire + Insert
	async updateDmPoints(props: GtoProperty[], whereClause?: string, ids?: any[]) {
		const retireProps:GtoProperty[] = [];
		props.forEach(p => {
			// Retire
			const retireProp: GtoProperty = {};
			retireProp[this.dmTranxDeleteColumn] = {
				type: this.appinfoService.columnMap[this.dmTranxDeleteColumn].type,
				value: '<p_session_id>'
			}
			retireProps.push(retireProp);
			// Insert
			p[this.dmTranxCreateColumn] = {
				type: this.appinfoService.columnMap[this.dmTranxCreateColumn].type,
				value: '<p_session_id>'
			}
		});
		const gtoRetire: Gto = {
			table: this.demarcationSourceTableName,
			operation: 'update',
			properties: retireProps
		};
		const gtoInsert: Gto = {
			table: this.demarcationSourceTableName,
			operation: 'insert',
			properties: props
		};
		const gtos = [gtoRetire, gtoInsert];
		if (ids && ids.length) {
			const idCol = this.appinfoService.columnMap[this.dmIdColumn];
			gtos.forEach(gto => {
				gto.keycol = idCol.name;
				gto.keytyp = idCol.type;
				gto.keyvals = ids;
			});
		}
		if (whereClause) {
			gtos.forEach(gto => gto.where = whereClause);
		}
		await this.gtoService.processGto(gtos);
	}




	testGto() {
		// Update by id
				// const props: GtoProperty[] = [{
				// 	mpt_reach: {
				// 		type: 'number',
				// 		value: 1
				// 	}
				// }];
				// this.updateDmPoints(props, null, ['{E075EBC3-9681-49F1-BDBE-8B5D967352A7}'])


		// // Update by where
				// const props2: GtoProperty[] = [{
				// 	mpt_reach: {
				// 		type: 'number',
				// 		value: 2
				// 	}
				// }];
				// // const where2 = 'mpt_reach = 1';
				// const where2 = 'mpt_guid in ()'
				// this.updateDmPoints(props2, where2);

		// Insert
			// const props: GtoProperty[] = [{
			// 	mpt_split_insert: {
			// 		type: 'text',
			// 		value: 'I'
			// 	},
			// 	mpt_resolved: {
			// 		type: 'bool',
			// 		value: false
			// 	},
			// 	mpt_state_cd: {
			// 		type: 'text',
			// 		value: 'AZ'
			// 	},
			// 	mpt_affected_agencies: {
			// 		type: 'jsonb',
			// 		value: [this.userService.getUserInfo().customerId]
			// 	},
			// 	mpt_reach: {
			// 		type: 'number',
			// 		value: 4
			// 	}
			// }];
			// const geomProp = this.addGtoProp('POINT(-12486322.98238229 3929551.187112625)', null, true, props[0]);
			// this.insertDmPoints(props)


		// Delete
			// {D41631AD-88EA-4874-83E0-EA98EE641AF7}

			// this.deleteDmPoints(null, ['D41631AD-88EA-4874-83E0-EA98EE641AF7']);
			

		// const gto: Gto = {
		// 	table: 'azgiv.demarcation_points',
		// 	operation: 'update',
		// 	keycol: 'mpt_guid',
		// 	keyvals: ['398F6F59-C422-4AE7-8A39-18F3F3FFCDE3'],
		// 	properties: [
		// 		{
		// 			mpt_reach: {
		// 				type: 'number',
		// 				value: 2
		// 			}
		// 			// 'mpt_comments': 'test comments'
		// 		}
		// 	]
		// };
		// this.gtoService.processGto([gto]);
	}




	// public async initVectorTileInfo() {
	// 	const dataSrid = 'EPSG:4326';
	// 	this.vectorTileInfo = {
	// 		srid: dataSrid,
	// 		extent: getProjection(dataSrid).getExtent(),
	// 		numGridLevel: 20,
	// 		grids: {
	// 			1: {
	// 				tileHeight: null,
	// 				tileWidth: null,
	// 				numTiles: null
	// 			},

	// 		}
	// 	};
	// }

	// public async getDemarcationPoints(mapExtent: number[]) {
	// 	const gridLevel = this.cMapService.calcGridLevel(this.vectorTileInfo.numGridLevel, this.vectorTileInfo.grids, mapExtent, this.vectorTileInfo.srid);
	// 	const tileRange = this.cMapService.calcVectorTileRangeByExtent(gridLevel, mapExtent, this.vectorTileInfo.extent, this.vectorTileInfo.srid);
	// 	const z = gridLevel + 1;

	// 	// Gfo to get tiles
	// 	const filter = this.gfoService.getFilter(this.dmAgencyColumn, this.gfoService.filterOperators.contains, this.userService.getUserInfo().customerId, 'jsonb');
	// 	const gfo = this.gfoService.getGfo(this.gfoService.filterLogics.and, [filter]);
	// 	//
	// 	const promises = [];
	// 	for (var i = tileRange.rowNumStart; i <= tileRange.rowNumEnd; i++) {
	// 		for (var j = tileRange.columnNumStart; j <= tileRange.columnNumEnd; j++) {
	// 			const promise = this.getVectorTile([z, j, i], '*', gfo)
	// 			promises.push(promise);
	// 		}
	// 	}
	// 	const features = [];
	// 	const format = new MVT({
	// 		featureClass: Feature
	// 	});
	// 	await Promise.all(promises).then(rspList => {
	// 		rspList.forEach(rsp => {
	// 			const data = rsp.data.content;
	// 			if (data.type === 'Buffer') {
	// 				const featsOnTile = format.readFeatures(data.data, {
	// 					extent: [], // TODO: ext 
	// 					featureProjection: getProjection('EPSG:3857'),
	// 				});
	// 				features.push(featsOnTile);
	// 			}
	// 		});
	// 	}).catch(err => {
	// 		console.log(err);
	// 		this.toastr.error(err.message);
	// 	});
	// 	return features;
	// }



	public vector_tile_experiment() {
		// const key = 'pk.eyJ1IjoiemhhbmdqaW56aG91OTMiLCJhIjoiY2p1c2xhcGNhMGI2bTRkcGUwZnVlMjc0ciJ9.kpM6GRviXU5Sllu3C7muHg';


		// // Calculation of tile urls
		// var resolutions = [];
		// for (var i = 0; i <= 8; ++i) {
		// resolutions.push(156543.03392804097 / Math.pow(2, i * 2));
		// }

		// const layer = new VectorTileLayer({
		// 	// source: new VectorTileSource({
		// 	// 	attributions: '© <a href="https://www.mapbox.com/map-feedback/">Mapbox</a> ' +
		// 	// 	  '© <a href="https://www.openstreetmap.org/copyright">' +
		// 	// 	  'OpenStreetMap contributors</a>',
		// 	// 	format: new MVT(),
		// 	// 	tileGrid: new TileGrid({
		// 	// 	  extent: getProjection('EPSG:3857').getExtent(),
		// 	// 	  resolutions: resolutions,
		// 	// 	  tileSize: 512
		// 	// 	}),
		// 	// 	tileUrlFunction: (tileCoord) => {
		// 	// 		return (
		// 	// 		  'https://{a-d}.tiles.mapbox.com/v4/mapbox.mapbox-streets-v6/' +
		// 	// 		  '{z}/{x}/{y}.vector.pbf?access_token=' +
		// 	// 		  key
		// 	// 		)
		// 	// 		  .replace('{z}', String(tileCoord[0] * 2 - 1))
		// 	// 		  .replace('{x}', String(tileCoord[1]))
		// 	// 		  .replace('{y}', String(tileCoord[2]))
		// 	// 		  .replace(
		// 	// 			'{a-d}',
		// 	// 			'abcd'.substr(((tileCoord[1] << tileCoord[0]) + tileCoord[2]) % 4, 1)
		// 	// 		  );
		// 	// 	}
		// 	// }),

		// 	// source: new VectorTileSource({
		// 	// 	crossOrigin: 'anonymous',
		// 	// 	format: new MVT(),
		// 	// 	// url: AppSettings.serviceRootUrl + 'azgiv/vectorTile',
		// 	// 	tileUrlFunction: async (tileCoord) => {
		// 	// 		// return 'https://www.linearbench.com/LBO_Download/ox2spf0rl5a'
		// 	// 		// debugger
		// 	// 		// console.log(tileCoord)
		// 	// 		const rsp = await this.getVectorTile();
		// 	// 		return rsp.content;
		// 	// 	},
		// 	// 	tileGrid: new TileGrid({
		// 	// 		extent: getProjection('EPSG:3857').getExtent(),
		// 	// 		resolutions: resolutions,	
		// 	// 	}),
		// 	// 	tilePixelRatio: 16
		// 	// }),

		// 	source: new VectorTileSource({
		// 		format: new MVT(),
		// 		url: 'currently_not_using_this_url',
		// 		tileLoadFunction: (tile: any, url) => {
		// 			tile.setLoader(async (ext, reso, proj) => {
		// 				let rsp = await this.getVectorTile();
		// 				rsp = rsp.content;
		// 				if (rsp.type === 'Buffer') {
		// 					const format = tile.getFormat();
		// 					const features = format.readFeatures(new Uint8Array(rsp.data), {
		// 						extent: ext,
		// 						featureProjection: proj,
		// 						dataProjection: getProjection('EPSG:4326')
		// 					});
		// 					tile.setFeatures(features);
		// 				}
		// 				// if (rsp.type === 'string') {
		// 				// 	const format = tile.getFormat();
		// 				// 	const typedArray = new Uint8Array(rsp.content.match(/[\da-f]{2}/gi).map(function (h) {
		// 				// 		return parseInt(h, 16)
		// 				// 	  }))
		// 				// 	const features = format.readFeature(typedArray.buffer, {
		// 				// 		extent: ext,
		// 				// 		featureProjection: proj
		// 				// 	});
		// 				// 	tile.setFeatures(features);
		// 				// }
		// 			})
		// 		}
		// 	}),

		// 	// source: new VectorTileSource({
		// 	// 	format: new MVT(),
		// 	// 	// url:
		// 	// 	// 	'https://{a-d}.tiles.mapbox.com/v4/mapbox.mapbox-streets-v6/' +
		// 	// 	// 	'{z}/{x}/{y}.vector.pbf?access_token=' +
		// 	// 	// 	key,
		// 	// 	url: 'https://www.linearbench.com/LBO_Download/ox2spf0rl5a'
		// 	// }),


		// 	style: (feature, res) => {
		// 		return new Style({
		// 			stroke: new Stroke({
		// 				width: 2,
		// 				color: 'rgba(0, 102, 204)'
		// 			})
		// 		})
		// 	}
		// });
		// return layer;
	}
}


// public addVectorTileBackgroundLayer(map: LRSMap.Map) {
// 	const source = new VectorTileSource({
// 		format: new MVT(),
// 		tileUrlFunction: function (tileCoord) {
// 			// Use the tile coordinate as a pseudo URL for caching purposes
// 			return JSON.stringify(tileCoord);
// 		},
// 		tileLoadFunction: (tile: any, url) => {
// 			tile.setLoader(async (ext, reso, proj) => {
// 				let rsp = await this.getVectorTile(tile.tileCoord);
// 				rsp = rsp.data.content;
// 				let features = [];
// 				if (rsp.type === 'Buffer') {
// 					const format = tile.getFormat();
// 					features = format.readFeatures(rsp.data, {
// 						extent: ext,
// 						featureProjection: proj,
// 						// dataProjection: getProjection('EPSG:4326')
// 					});
// 				}
// 				tile.setFeatures(features);
// 			});
// 		}
// 	});
// 	const layer = new VectorTileLayer({
// 		source: source,
// 		style: (feature, res) => {
// 			return new Style({
// 				stroke: new Stroke({
// 					width: 2,
// 					color: 'rgba(0, 102, 204)'
// 				}),
// 				image: new Circle({
// 					radius: 4,
// 					fill: new Fill({
// 						color: 'gray'
// 					})
// 				})
// 			})
// 		}
// 	});
// 	map.olMap.addLayer(layer);
// 	map.addedLayers[this.demarcationBackgroundLayerName] = layer;
// 	return layer;
// }


// public addVectorTileDemarcationLayer(map: LRSMap.Map, cols: string) {
// 	// Gfo to get tiles
// 	const filter = this.gfoService.getFilter(this.dmAgencyColumn, this.gfoService.filterOperators.contains, this.userService.getUserInfo().customerId, 'jsonb');
// 	const gfo = this.gfoService.getGfo(this.gfoService.filterLogics.and, [filter]);
// 	// Source
// 	const source = new VectorTileSource({
// 		format: new MVT({
// 			featureClass: Feature
// 		}),
// 		tileUrlFunction: function (tileCoord) {
// 			// Use the tile coordinate as a pseudo URL for caching purposes
// 			return JSON.stringify(tileCoord);
// 		},
// 		tileLoadFunction: async (tile: any, url) => {
// 			await new Promise((resolve, reject) => {
// 				tile.setLoader(async (ext, reso, proj) => {
// 					const rsp = await this.getVectorTile(tile.tileCoord, cols, gfo);
// 					const data = rsp.data.content;
// 					let features = [];
// 					if (data.type === 'Buffer') {
// 						const format = tile.getFormat();
// 						features = format.readFeatures(data.data, {
// 							extent: ext,
// 							featureProjection: proj,
// 							// dataProjection: getProjection('EPSG:4326')
// 						});
// 					}
// 					features.forEach(f => f.setProperties({tileCoord: tile.tileCoord}))
// 					tile.setFeatures(features);
// 					console.log('tile: ' + tile.tileCoord, features.length)
// 					console.log('getFeatures: ', tile.getFeatures().length)
// 					resolve()
// 				});
// 			});
// 		}
// 	});
// 	// To check tile load ready
// 	source.on('tileloadstart', () => this.numOfInFlightTiles++);
// 	source.on('tileloadend', () => this.numOfInFlightTiles--);
// 	source.on('tileloaderror', () => this.numOfInFlightTiles--);
// 	const layer = new VectorTileLayer({
// 		// Reference: https://openlayers.org/en/latest/examples/geojson-vt.html
// 		source: source,
// 		cacheSize: 0,
// 		style: (feature, res) => {
// 			return new Style({
// 				stroke: new Stroke({
// 					width: 2,
// 					color: 'rgba(0, 102, 204)'
// 				}),
// 				image: new Circle({
// 					radius: 4,
// 					fill: new Fill({
// 						color: 'blue'
// 					})
// 				})
// 			})
// 		}
// 	});
// 	map.olMap.addLayer(layer);
// 	map.addedLayers[this.demarcationLayerName] = layer;
// 	return layer;
// }
// public addSelectionLayer(map: LRSMap.Map) {
// 	const color = 'yellow';
// 	map.addLayer(this.demarcationSelectionLayerName, {
// 		style: new Style ({
// 			image: new Circle({
// 				radius: 10,
// 				fill: new Fill({
// 					color: color
// 				})
// 			}),
// 			stroke: new Stroke({
// 				width: 10,
// 				color: color
// 			})
// 		})
// 	});
// }
